import { useState } from "react";
import type { DateRange as DayPickerRange } from "react-day-picker";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

/**
 * M27c — one date-range filter, shared by trades history and signals.
 *
 * Everything emits HALF-OPEN [from, to): `from` is a local start-of-day, `to` is the start
 * of the day AFTER the last included one. That is exactly what the M27a endpoints compare
 * with (`closedAt >= from AND closedAt < to`), so a boundary close lands in exactly one of
 * two adjacent windows — never both, never neither.
 */
export type DateRange = { from: Date; to: Date };

export type PresetName = "7d" | "30d" | "month";

/** Pure so the window arithmetic is testable without a DOM or a mocked clock. */
export function presetRange(name: PresetName, now: Date): DateRange {
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  // Start of TOMORROW — the exclusive bound that still includes all of today.
  const tomorrow = new Date(year, month, day + 1);
  switch (name) {
    case "7d":
      // Six days back plus today = seven; the Date constructor normalizes negative days.
      return { from: new Date(year, month, day - 6), to: tomorrow };
    case "30d":
      return { from: new Date(year, month, day - 29), to: tomorrow };
    case "month":
      return { from: new Date(year, month, 1), to: new Date(year, month + 1, 1) };
  }
}

const PRESETS: { name: PresetName; label: string }[] = [
  { name: "7d", label: "7d" },
  { name: "30d", label: "30d" },
  { name: "month", label: "This month" },
];

export function DateRangePicker({
  value,
  onChange,
}: {
  value: DateRange | null;
  onChange: (range: DateRange | null) => void;
}) {
  // Which chip is lit. Derived-from-value comparisons are fragile (three presets can share
  // an instant at month edges); remembering the click is honest and enough.
  const [active, setActive] = useState<PresetName | "custom" | null>(value === null ? null : "custom");
  const [open, setOpen] = useState(false);
  // The in-progress calendar selection; only a COMPLETE from+to is ever emitted.
  const [draft, setDraft] = useState<DayPickerRange | undefined>();

  const pick = (name: PresetName) => {
    setActive(name);
    onChange(presetRange(name, new Date()));
  };

  const clear = () => {
    setActive(null);
    setDraft(undefined);
    onChange(null);
  };

  const chip = (selected: boolean) =>
    cn(
      "rounded-full border px-2.5 py-1 text-xs transition-colors",
      selected ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground",
    );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button type="button" onClick={clear} className={chip(active === null)}>
        All time
      </button>
      {PRESETS.map((preset) => (
        <button key={preset.name} type="button" onClick={() => pick(preset.name)} className={chip(active === preset.name)}>
          {preset.label}
        </button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger className={cn(chip(active === "custom"), "inline-flex items-center gap-1")}>
          <CalendarDays className="h-3 w-3" />
          {active === "custom" && value
            ? // The picked span, with the exclusive bound walked back a day for display —
              // Andy picked "…to the 19th", not "…to before the 20th".
              `${value.from.toLocaleDateString()} – ${new Date(value.to.getTime() - 1).toLocaleDateString()}`
            : "Custom"}
        </PopoverTrigger>
        <PopoverContent>
          <Calendar
            mode="range"
            selected={draft}
            onSelect={(range: DayPickerRange | undefined) => {
              setDraft(range);
              if (range?.from && range.to) {
                setActive("custom");
                onChange({
                  from: new Date(range.from.getFullYear(), range.from.getMonth(), range.from.getDate()),
                  // Day AFTER the picked end — inclusive pick, exclusive bound.
                  to: new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate() + 1),
                });
                setOpen(false);
              }
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
